// =============================================================================
// CUB/m² — Custo Unitário Básico da Construção Civil
// =============================================================================
// Fonte primária: SINDUSCON de cada estado, via CBIC (cub.org.br) e MySide
// Última atualização: Fevereiro/2026
//
// TIPOS DE DADOS:
// - R8-N: Valor OFICIAL coletado do SINDUSCON de cada estado (via MySide.com.br)
// - Outros tipos (R1-B, R1-N, R1-A, R8-A, R16-N, GI, RP1Q):
//   * SP: Dados OFICIAIS completos (Portal do RI / SINDUSCON-SP, Fev/2026)
//   * Demais estados: Derivados a partir do R8-N usando proporções do SP
//     (as proporções entre tipos CUB são relativamente estáveis entre estados)
//
// ESTADOS SEM DADOS MYSDE (AC, AM, RR, SE, TO):
//   R8-N estimado a partir da média regional CBIC + ponderação por participação
// ESTADOS COM DADOS SINDUSCON DIRETO (AL, PB, AP):
//   R8-N oficial coletado dos PDFs dos SINDUSCONs estaduais via Google Docs Viewer
//
// O script /scripts/atualizar-cub.js automatiza a coleta mensal.
// =============================================================================

// Proporções SP (Fev/2026) - base para derivar tipos nos demais estados
// Calculadas como: tipo / R8-N
const SP_RATIOS = {
  'R1-B': 0.9820,  // 2093.44 / 2131.81
  'R1-N': 1.2021,  // 2562.62 / 2131.81
  'R1-A': 1.4559,  // 3103.95 / 2131.81
  'R8-N': 1.0000,
  'R8-A': 1.1749,  // 2504.63 / 2131.81
  'R16-N': 0.9721, // 2072.34 / 2131.81
  'GI':   0.5662,  // 1207.27 / 2131.81
  'RP1Q': 1.0671,  // 2274.81 / 2131.81
};

// Gera todos os tipos a partir do R8-N usando proporções SP
function derivarTipos(r8n) {
  return {
    'R1-B': { valor: Math.round(r8n * SP_RATIOS['R1-B'] * 100) / 100, descricao: 'Residência Unifamiliar - Padrão Baixo' },
    'R1-N': { valor: Math.round(r8n * SP_RATIOS['R1-N'] * 100) / 100, descricao: 'Residência Unifamiliar - Padrão Normal' },
    'R1-A': { valor: Math.round(r8n * SP_RATIOS['R1-A'] * 100) / 100, descricao: 'Residência Unifamiliar - Padrão Alto' },
    'R8-N': { valor: r8n, descricao: 'Prédio 8 Pavimentos - Padrão Normal' },
    'R8-A': { valor: Math.round(r8n * SP_RATIOS['R8-A'] * 100) / 100, descricao: 'Prédio 8 Pavimentos - Padrão Alto' },
    'R16-N': { valor: Math.round(r8n * SP_RATIOS['R16-N'] * 100) / 100, descricao: 'Prédio 16 Pavimentos - Padrão Normal' },
    'GI':   { valor: Math.round(r8n * SP_RATIOS['GI'] * 100) / 100, descricao: 'Galpão Industrial' },
    'RP1Q': { valor: Math.round(r8n * SP_RATIOS['RP1Q'] * 100) / 100, descricao: 'Residência Popular (1 Quarto)' },
  };
}

const cubData = {
  data_referencia: '2026-02',       // Mês de referência dos dados
  data_atualizacao: '2026-04-02',   // Data da última atualização do arquivo
  fonte: 'SINDUSCON estaduais via MySide.com.br / Portal do RI / CBIC',
  notas: 'SP: dados oficiais completos. Demais: R8-N oficial, outros tipos derivados via proporção SP.',
  estados: {
    // =========================================================================
    // DADOS OFICIAIS COMPLETOS — SP (Portal do RI / SINDUSCON-SP, Fev/2026)
    // =========================================================================
    SP: {
      nome: 'São Paulo',
      sinduscon: 'SINDUSCON-SP',
      fonte_r8n: 'oficial',
      fonte_tipos: 'oficial',
      tipos: {
        'R1-B': { valor: 2093.44, descricao: 'Residência Unifamiliar - Padrão Baixo' },
        'R1-N': { valor: 2562.62, descricao: 'Residência Unifamiliar - Padrão Normal' },
        'R1-A': { valor: 3103.95, descricao: 'Residência Unifamiliar - Padrão Alto' },
        'R8-N': { valor: 2131.81, descricao: 'Prédio 8 Pavimentos - Padrão Normal' },
        'R8-A': { valor: 2504.63, descricao: 'Prédio 8 Pavimentos - Padrão Alto' },
        'R16-N': { valor: 2072.34, descricao: 'Prédio 16 Pavimentos - Padrão Normal' },
        'GI':   { valor: 1207.27, descricao: 'Galpão Industrial' },
        'RP1Q': { valor: 2274.81, descricao: 'Residência Popular (1 Quarto)' },
      },
    },

    // =========================================================================
    // R8-N OFICIAL (MySide/SINDUSCON) + tipos derivados via proporção SP
    // =========================================================================
    RJ: {
      nome: 'Rio de Janeiro',
      sinduscon: 'SINDUSCON-Rio',
      fonte_r8n: 'oficial',
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(2390.16),
    },
    MG: {
      nome: 'Minas Gerais',
      sinduscon: 'SINDUSCON-MG',
      fonte_r8n: 'oficial',
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(2504.80),
    },
    RS: {
      nome: 'Rio Grande do Sul',
      sinduscon: 'SINDUSCON-RS',
      fonte_r8n: 'oficial',
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(2757.37),
    },
    PR: {
      nome: 'Paraná',
      sinduscon: 'SINDUSCON-PR',
      fonte_r8n: 'oficial',
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(2582.23),
    },
    SC: {
      nome: 'Santa Catarina',
      sinduscon: 'SINDUSCON-Florianópolis',
      fonte_r8n: 'oficial',
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(3028.45),
    },
    BA: {
      nome: 'Bahia',
      sinduscon: 'SINDUSCON-BA',
      fonte_r8n: 'oficial',
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(2089.47),
    },
    PE: {
      nome: 'Pernambuco',
      sinduscon: 'SINDUSCON-PE',
      fonte_r8n: 'oficial',
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(2127.38), // MySide Fev/2026 (atualizado)
    },
    GO: {
      nome: 'Goiás',
      sinduscon: 'SINDUSCON-GO',
      fonte_r8n: 'oficial',
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(2716.76),
    },
    DF: {
      nome: 'Distrito Federal',
      sinduscon: 'SINDUSCON-DF',
      fonte_r8n: 'oficial',
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(2259.90),
    },
    CE: {
      nome: 'Ceará',
      sinduscon: 'SINDUSCON-CE',
      fonte_r8n: 'oficial',
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(2284.42),
    },
    PA: {
      nome: 'Pará',
      sinduscon: 'SINDUSCON-PA',
      fonte_r8n: 'oficial',
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(2269.15),
    },
    ES: {
      nome: 'Espírito Santo',
      sinduscon: 'SINDUSCON-ES',
      fonte_r8n: 'oficial',
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(2800.10),
    },
    MA: {
      nome: 'Maranhão',
      sinduscon: 'SINDUSCON-MA',
      fonte_r8n: 'oficial',
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(1866.08),
    },
    MT: {
      nome: 'Mato Grosso',
      sinduscon: 'SINDUSCON-MT',
      fonte_r8n: 'oficial',
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(3128.37),
    },
    MS: {
      nome: 'Mato Grosso do Sul',
      sinduscon: 'SINDUSCON-MS',
      fonte_r8n: 'oficial',
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(1700.53),
    },
    PI: {
      nome: 'Piauí',
      sinduscon: 'SINDUSCON-PI',
      fonte_r8n: 'oficial',
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(2951.49),
    },
    RN: {
      nome: 'Rio Grande do Norte',
      sinduscon: 'SINDUSCON-RN',
      fonte_r8n: 'oficial',
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(1981.73),
    },
    RO: {
      nome: 'Rondônia',
      sinduscon: 'SINDUSCON-RO',
      fonte_r8n: 'oficial',
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(2334.56),
    },

    // =========================================================================
    // R8-N OFICIAL — coletado diretamente dos PDFs dos SINDUSCONs estaduais
    // =========================================================================
    AL: {
      nome: 'Alagoas',
      sinduscon: 'SINDUSCON-AL',
      fonte_r8n: 'oficial',          // PDF SINDUSCON-AL via FIEA, Fev/2026
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(1960.43),
    },
    PB: {
      nome: 'Paraíba',
      sinduscon: 'SINDUSCON-JP',
      fonte_r8n: 'oficial',          // PDF SINDUSCONJP, Fev/2026
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(1650.86),
    },
    AP: {
      nome: 'Amapá',
      sinduscon: 'SINDUSCON-AP',
      fonte_r8n: 'oficial',          // PDF SINDUSCON-AP, Jan/2026
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(2640.87),
    },

    // =========================================================================
    // ESTADOS SEM DADOS ONLINE — R8-N estimado via média regional CBIC
    // SINDUSCONs sem site acessível, fora do ar, ou com proteção anti-bot
    // Atualizar manualmente quando os SINDUSCONs publicarem dados acessíveis
    // =========================================================================
    AC: {
      nome: 'Acre',
      sinduscon: 'SINDUSCON-AC',
      fonte_r8n: 'estimado_cbic',    // Sem site SINDUSCON-AC online
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(2180.00),
    },
    AM: {
      nome: 'Amazonas',
      sinduscon: 'SINDUSCON-AM',
      fonte_r8n: 'estimado_cbic',    // sinduscon-am.org.br fora do ar
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(2250.00),
    },
    RR: {
      nome: 'Roraima',
      sinduscon: 'SINDUSCON-RR',
      fonte_r8n: 'estimado_cbic',    // sinduscon-rr.org.br com proteção anti-bot
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(2200.00),
    },
    SE: {
      nome: 'Sergipe',
      sinduscon: 'SINDUSCON-SE',
      fonte_r8n: 'estimado_cbic',    // SINDUSCON-SE não publica CUB online
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(1920.00),
    },
    TO: {
      nome: 'Tocantins',
      sinduscon: 'SINDUSCON-TO',
      fonte_r8n: 'estimado_cbic',    // SINDUSCON-TO só tem Facebook
      fonte_tipos: 'derivado_sp',
      tipos: derivarTipos(2100.00),
    },
  },
};

// =============================================================================
// EXPORTS AUXILIARES (mantidos para compatibilidade)
// =============================================================================

export const CUB_EXTRAS = {
  // Engenharia / Construção (exibidos na página 3)
  fundacao_especial: { label: 'Fundação especial (estaca/hélice)', tipo: 'fixo', valor: 85000, descricao: 'Estimativa para fundação profunda', categoria: 'engenharia' },
  elevador: { label: 'Elevador', tipo: 'unitario', valor: 120000, descricao: 'Por unidade de elevador', categoria: 'engenharia' },
  projeto_arquitetonico: { label: 'Projeto arquitetônico', tipo: 'percentual', valor: 0.04, descricao: '~4% do custo da obra', categoria: 'engenharia' },
  muro_portao: { label: 'Muro e portão', tipo: 'fixo', valor: 35000, descricao: 'Muro de divisa + portão', categoria: 'engenharia' },
  cisterna: { label: 'Cisterna / Caixa d\'água extra', tipo: 'fixo', valor: 4000, descricao: 'Cisterna ou reservatório extra', categoria: 'engenharia' },
  energia_solar: { label: 'Energia Solar (Fotovoltaica)', tipo: 'fixo', valor: 18000, descricao: 'Sistema fotovoltaico básico', categoria: 'engenharia' },
  // Acabamento / Arquitetura (exibidos na página 2)
  churrasqueira: { label: 'Churrasqueira', tipo: 'fixo', valor: 5000, descricao: 'Churrasqueira de alvenaria', categoria: 'acabamento' },
  piscina: { label: 'Piscina', tipo: 'fixo', valor: 65000, descricao: 'Piscina de alvenaria média', categoria: 'acabamento' },
  paisagismo: { label: 'Paisagismo', tipo: 'fixo', valor: 25000, descricao: 'Paisagismo básico', categoria: 'acabamento' },
  automacao: { label: 'Automação Residencial', tipo: 'fixo', valor: 12000, descricao: 'Automação básica residencial', categoria: 'acabamento' },
  ar_condicionado: { label: 'Ar-condicionado (infraestrutura)', tipo: 'por_m2', valor: 85, descricao: 'Por m² de área climatizada', categoria: 'acabamento' },
};

export const AREA_COEFICIENTES = {
  garagem: { label: 'Garagem / Estacionamento', coeficiente: 0.50 },
  varanda: { label: 'Varanda / Sacada', coeficiente: 0.75 },
  subsolo: { label: 'Subsolo', coeficiente: 0.50 },
};

export const TIPO_LABELS = {
  'R1-B': 'Casa térrea - Padrão Baixo',
  'R1-N': 'Casa térrea - Padrão Normal',
  'R1-A': 'Casa térrea - Padrão Alto',
  'R8-N': 'Prédio 8 andares - Normal',
  'R8-A': 'Prédio 8 andares - Alto',
  'R16-N': 'Prédio 16 andares - Normal',
  'GI': 'Galpão Industrial',
  'RP1Q': 'Casa Popular',
};

// Helper: retorna lista de estados com fonte oficial vs estimado
export function getEstadosStatus() {
  return Object.entries(cubData.estados).reduce((acc, [uf, data]) => {
    acc[uf] = {
      nome: data.nome,
      r8n_oficial: data.fonte_r8n === 'oficial',
      tipos_oficial: data.fonte_tipos === 'oficial',
    };
    return acc;
  }, {});
}

export default cubData;
